// src/components/features/clients/ClientLogoUpload.jsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Business as BusinessIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import SecureAvatar from '../../../components/ui/SecureAvatar';

// Client logo upload component
const ClientLogoUpload = ({ 
  logo, 
  clientName, 
  onUpload, 
  onRemove,
  isUploading = false,
  error = null
}) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Handle click on upload button
  const handleUploadClick = () => {
    fileInputRef.current.click();
  };
  
  // Handle file selection
  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files.length) {
      onUpload(files[0]);
    }
    
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };
  
  // Handle drag events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length) {
      onUpload(files[0]);
    }
  };
  
  // Handle logo removal
  const handleRemove = () => {
    if (onRemove) {
      onRemove();
    }
  };
  
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        Client Image
      </Typography>
      
      {logo ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SecureAvatar
            src={logo instanceof File ? URL.createObjectURL(logo) : logo?.key || logo?.url}
            // alt={clientName}
            sx={{
              width: 80,
              height: 80,
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            }}
          >
            {clientName?.charAt(0) || <BusinessIcon />}
          </SecureAvatar>
          
          <Box>
            <Typography variant="body2" noWrap>
              {logo.originalname || 'Uploaded logo'}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleUploadClick}
                disabled={isUploading}
              >
                Change
              </Button>
              
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={handleRemove}
                disabled={isUploading}
                startIcon={<DeleteIcon />}
              >
                Remove
              </Button>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
            borderRadius: 2,
            p: 3,
            transition: 'all 0.3s ease',
            backgroundColor: isDragging ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
            textAlign: 'center',
            cursor: 'pointer',
          }}
          onClick={handleUploadClick}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {isUploading ? (
            <CircularProgress size={40} />
          ) : (
            <>
              <UploadIcon 
                sx={{ 
                  fontSize: 40, 
                  color: theme.palette.text.secondary, 
                  mb: 1 
                }} 
              />
              <Typography variant="body1" gutterBottom>
                Drop your logo here or click to browse
              </Typography>
              <Typography variant="body2" color="textSecondary">
                PNG, JPG, or JPEG (max 2MB)
              </Typography>
              
              {error && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {error}
                </Typography>
              )}
            </>
          )}
        </Box>
      )}
      
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/png, image/jpeg, image/jpg"
        style={{ display: 'none' }}
      />
    </Box>
  );
};

export default ClientLogoUpload;